import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import moment from "moment"
import * as orderApis from "../../utilities/apis/order"
import * as dispatcherServices from "../../utilities/services/dispatcher"
import { getOrderStatus, getPaymentStatus, isDeal } from "../../utilities/enumerations/constants"
import { getShippingStatus } from "../../utilities/enumerations/functions"
import Layout from "../../components/Layout"

const ProductCell = ({ item }) => {
  const [view, setView] = useState(false)

  return (
    <td className="align-middle">
      <div className="d-flex gap-2 align-items-center">
        {isDeal(item.type) && <button className="btn btn-sm btn-secondary p-1 py-0" onClick={() => setView(!view)}>
          <i className={`bi bi-chevron-${view ? "down" : "right"}`}></i>
        </button>}
        {item.title}
      </div>
      {view && isDeal(item.type) && <ul className="list-unstyled m-0 mt-1">
        {item.products.map((product) => (<li key={`${item.id}-${product.id}`}>- {product.title} x{product.order_quantity}</li>))}
      </ul>}
    </td>
  )
}


const Order = () => {
  const navigate = useNavigate()
  const { token, permissions } = useSelector(state => state.user)
  const params = new URLSearchParams(window.location.search)
  const [order, setOrder] = useState(null)

  const handleConfirm = async () => {
    try {
      const response = await dispatcherServices.orderPunched(token, {
        order_id: params.get("id")
      })
      if (!response.status) throw new Error(response.message)

      setOrder({
        ...order,
        order_status: "punched"
      })
    } catch (error) {
      alert(error.message)
    }
  }

  const handlePay = async () => {
    try {
      const response = await orderApis.payOrder(token, params.get("id"))
      if (!response.status) throw new Error(response.message)

      setOrder({
        ...order,
        payment_status: "paid"
      })
    } catch (error) {
      alert(error.message)
    }
  }

  const getOrder = async () => {
    try {
      const response = await orderApis.getOrder(token, params.get("id"))
      if (!response.status) throw new Error(response.message)

      setOrder(response.order)
    } catch (error) {
      alert(error.message)
    }
  }

  useEffect(() => {
    if (params.has("id"))
      getOrder()
  }, [])

  return (
    <Layout
      header={true}
      footer={true}
    >
      <main id="order" className="py-4">
        <div className="w-75 mx-auto">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-3 align-items-center">
              <button type="button" className="btn btn-secondary rounded-4" onClick={() => navigate(-1)}><i className="bi bi-chevron-left"></i></button>
              <div>
                <div className="d-flex gap-3 align-items-start">
                  <p className="lead text-body-emphasis fw-bold m-0">Order #P{order?.id}</p>
                  <p className="m-0">{getPaymentStatus[order?.payment_status]}</p>
                  <p className="m-0">{getOrderStatus[order?.order_status]}</p>
                  <p className="m-0"><span className={`badge rounded-3 p-2 ${getShippingStatus(order?.ship_datetime).class}`}>{getShippingStatus(order?.ship_datetime).status}</span></p>
                </div>
                <p className="text-body-secondary m-0">{order?.order_datetime ? `${moment(order.order_datetime).format("DD MMMM YYYY")} at ${moment(order.order_datetime).format("hh:mm a")}` : ""}</p>
              </div>
            </div>
            {permissions["PAY_ORDER"] && <div className="d-flex gap-2">
              {order?.order_status === "pending" && <button className="btn btn-theme rounded-4" onClick={() => handleConfirm()}>Confirm Order</button>}
              {order?.order_status !== "pending" && order?.payment_status !== "paid" && <button className="btn btn-theme rounded-4" onClick={() => handlePay()}>Mark as Paid</button>}
            </div>}
          </div>

          {order?.customer_note && <div className="alert alert-warning d-flex gap-3 align-items-center rounded-4 mb-3">
            <i className="lead bi bi-sticky"></i>
            <div>
              <p className="lead fw-bold m-0">Note</p>
              <p className="m-0">{order?.customer_note}</p>
            </div>
          </div>}

          <div className="row g-3">
            <div className="col-md-8">
              <div className="card shadow-none rounded-4 mb-3">
                <div className="card-body py-4">
                  <table className="table shadow-none m-0">
                    <thead>
                      <tr>
                        <th scope="col" style={{ width: "60%" }}>Product</th>
                        <th scope="col" className="text-center">Unit Price</th>
                        <th scope="col" className="text-center">Quantity</th>
                        <th scope="col" className="text-center">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order?.order_lineitems.map((item) => {
                        const swap = isDeal(item.type)
                          ? item.products.reduce((previous, current) => previous += current.swap || 0, 0)
                          : 0

                        return (
                          <tr key={item.id.toString()}>
                            <ProductCell item={item} />
                            <td className="align-middle text-center">Rs. {(item.price - (item.discount || 0)).toFixed(2)}</td>
                            <td className="align-middle text-center">{item.quantity}</td>
                            <td className="align-middle text-center">Rs. {(((item.price - (item.discount || 0)) + swap)).toFixed(2)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="card shadow-none rounded-4">
                <div className="card-body py-4">
                  <div className="border border-bottom-0 rounded-top-3 p-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <p className="text-body-tertiary fw-bold m-0">Sub Total</p>
                      <p className="text-body-tertiary fw-bold m-0">Rs. {order?.total.toFixed(2)}</p>
                    </div>
                    <div className="d-flex justify-content-between align-items-center">
                      <p className="text-body-tertiary fw-bold m-0">Discount</p>
                      <p className="text-body-tertiary fw-bold m-0">Rs. {order?.discount.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="d-flex justify-content-between align-items-center bg-light border p-3 rounded-bottom-3">
                    <p className="text-body-emphasis fw-bold m-0">Total</p>
                    <p className="text-body-emphasis fw-bold m-0">Rs. {(order?.total - order?.discount).toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card rounded-4 shadow-none mb-3">
                <div className="card-body py-4">
                  <p className="lead text-body-emphasis fw-bold">Note</p>
                  <p className="text-body-secondary fw-semibold m-0">{order?.customer_note || 'No notes from customer'}</p>
                </div>
              </div>

              <div className="card rounded-4 shadow-none mb-3">
                <div className="card-body py-4">
                  <p className="lead text-body-emphasis fw-bold">Rider</p>

                  <div className="d-flex gap-3 align-items-center">
                    <div className="driver-icon bg-body-secondary rounded-3">
                      <i className="bi bi-truck text-body-secondary"></i>
                    </div>
                    <div>
                      <p className="text-body-secondary fw-semibold m-0">{order?.driver?.name || 'Not assigned'}</p>
                      <p className="small text-body-tertiary fw-semibold m-0">{order?.driver?.username}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card rounded-4 shadow-none">
                <div className="card-body py-4">
                  <div className="mb-3">
                    <p className="lead text-body-emphasis fw-bold m-0">Customer</p>
                    <p className="text-body-secondary fw-semibold m-0">{order?.customer?.name}</p>
                  </div>
                  <div className="mb-3">
                    <p className="text-body-tertiary fw-bold m-0">Contact Information</p>
                    <p className="text-body-secondary fw-semibold m-0">{order?.customer?.mobile}</p>
                  </div>
                  <div>
                    <p className="text-body-tertiary fw-bold m-0">Shipping Address</p>
                    <p className="text-body-secondary fw-semibold m-0">{order?.customer?.address}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </Layout>
  )
}

export default Order
